/* eslint-disable @typescript-eslint/no-explicit-any */
import { type CSSProperties, type ReactNode, useRef } from "react";
import styles from "./RadioButtons.module.scss";

export type RadioOptionProps = {
  value: any;
  checked: boolean;
  onChange: () => void;
  children: ReactNode;
  /** Shared across options in a group so arrow keys move between them. */
  name?: string;
  dark?: boolean;
  /** Added to the wrapper, alongside the built-in classes. */
  className?: string;
  style?: CSSProperties;
  labelClassName?: string;
  labelStyles?: CSSProperties;
};

/** A single styled radio input. Use on its own if you need custom layout/styling options not provided by `RadioButtons`. */
export function RadioOption({
  value,
  checked,
  onChange,
  children,
  name,
  dark,
  className,
  style,
  labelClassName,
  labelStyles,
}: RadioOptionProps) {
  const labelRef = useRef<HTMLLabelElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      className={[styles.radio, dark && styles.dark, className]
        .filter(Boolean)
        .join(" ")}
      style={style}
      // Clicks on the wrapper's padding go through the input so it fires a native
      // change event (parent forms rely on it to detect unsaved changes). Clicks
      // inside the label already reach the input, so handling them here too would
      // mark it checked first and the browser would then skip the change event.
      onClick={(e) => {
        if (!labelRef.current?.contains(e.target as Node)) {
          inputRef.current?.click();
        }
      }}
    >
      <label
        ref={labelRef}
        className={[styles.customRadioButton, labelClassName]
          .filter(Boolean)
          .join(" ")}
        style={labelStyles}
      >
        <input
          ref={inputRef}
          type="radio"
          name={name}
          value={value}
          checked={checked}
          onChange={onChange}
        />
        <span className={styles.customRadio} aria-hidden="true">
          {checked && <div className={styles.dot}></div>}
        </span>
        {children}
      </label>
    </div>
  );
}
